import {HiChevronLeft, HiChevronRight} from 'react-icons/hi2'
import React, {PropsWithChildren, useMemo} from 'react'
import {BlazeConfig} from 'blaze-slider'
import {useTranslations} from 'use-intl'
import {useIsClient} from 'usehooks-ts'
import clsx from 'clsx'
import useBlazeSlider from '~/hooks/useBlazeSlider'

type CarouselProps = PropsWithChildren<{
    className?: string
    config?: BlazeConfig
    showDots?: boolean
    showArrows?: boolean
    showDisabledArrows?: boolean
    initialSlidesToShow?: number
}>

const Carousel = ({
    className,
    children,
    config = {
        all: {
            loop: false,
            slidesToShow: 1,
        },
    },
    showDots = true,
    showArrows = true,
    showDisabledArrows = false,
    initialSlidesToShow,
}: CarouselProps) => {
    const t = useTranslations('carousel')
    const {
        elRef: carouselRef,
        totalSlides,
        currentSlide,
        slider,
    } = useBlazeSlider(config, [config, children])
    const isClient = useIsClient()

    const hasPreviousSlide = (slider?.states[slider?.stateIndex ?? 0].prev.moveSlides ?? 0) > 0
    const hasNextSlide = (slider?.states[slider?.stateIndex ?? 0].next.moveSlides ?? 0) > 0

    const slidesToShowBeforeInit = useMemo(() => getSlidesToShow(config), [config])

    return (
        <div
            className={clsx('blaze-slider h-full', className)}
            style={
                {
                    '--slides-to-show': isClient
                        ? (slidesToShowBeforeInit ?? 1)
                        : (initialSlidesToShow ?? config.all.slidesToShow ?? 1),
                } as React.CSSProperties
            }
            ref={carouselRef}
        >
            {showArrows && (hasPreviousSlide || showDisabledArrows) && (
                <div className="absolute h-full left-1 top-0 flex items-center z-10">
                    <button
                        className="w-8 h-12 disabled:opacity-20"
                        disabled={!hasPreviousSlide}
                        onClick={() => slider?.prev()}
                    >
                        <div className="size-8 p-1 flex items-center justify-center rounded-full bg-white/90 text-gray-800">
                            <HiChevronLeft aria-hidden="true" className="size-full" />
                            <span className="sr-only">{t('previous')}</span>
                        </div>
                    </button>
                </div>
            )}
            <div className="blaze-container h-full">
                <div className="blaze-track-container h-full">
                    <div className="blaze-track h-full">{children}</div>
                </div>
            </div>
            {showArrows && (hasNextSlide || showDisabledArrows) && (
                <div className="absolute h-full right-1 top-0 flex items-center z-10">
                    <button
                        className="w-8 h-12 disabled:opacity-20"
                        disabled={!hasNextSlide}
                        onClick={() => slider?.next()}
                    >
                        <div className="size-8 p-1 flex items-center justify-center rounded-full bg-white/90  text-gray-800">
                            <HiChevronRight aria-hidden="true" className="size-full" />
                            <span className="sr-only">{t('next')}</span>
                        </div>
                    </button>
                </div>
            )}
            {showDots && totalSlides > 1 && (
                <ul className="flex justify-center items-center gap-1.5 pt-3 pb-4" role="tablist">
                    {[...new Array(totalSlides)].map((_, index) => (
                        <li key={index} role="presentation">
                            <button
                                role="tab"
                                aria-selected={index === currentSlide}
                                aria-label={`${index + 1} / ${totalSlides}`}
                                onClick={() => slider?.goToSlide(index)}
                                className={clsx(
                                    'size-2 block rounded-full shadow-sm',
                                    index === currentSlide ? 'bg-comet' : 'bg-white/50'
                                )}
                            />
                        </li>
                    ))}
                </ul>
            )}
        </div>
    )
}

const IS_SERVER = typeof window === 'undefined'

const getMatches = (query: string): boolean => {
    if (IS_SERVER) {
        return false
    }
    return window.matchMedia(query).matches
}

const getSlidesToShow = (config: BlazeConfig) => {
    const {all, ...otherSizes} = config
    const [_, option] = Object.entries(otherSizes)
        .reverse()
        .find(([size]) => {
            return getMatches(size)
        }) ?? ['all', all]
    return option.slidesToShow
}

export default Carousel
